import React, { useEffect, useState } from "react";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import makeStyles from "@material-ui/core/styles/makeStyles";
import {
    IAttribute,
    ImagePermissionType,
    IPrintedImagePermission,
} from "constants/product-customizations/customization";

import {
    CustomizationSelectionType,
    TCustomizationSelection,
} from "constants/product-customizations/customization-selection.types";

import { connect, Dispatch } from "react-redux";
import { getInitialImageGroup, getUserPermissions } from "./helpers";
import { Box } from "@material-ui/core";
import { format } from "lib/globalize-util";
import { TabPanel } from "./tab-panel";
import { IStore } from "reducers";
import { ImagesList } from "./images-list";
import { ICustomizationImageEntity } from "epics/customizations-epic";
import { IProduct } from "records/item/item";
import { resetCheckedImageTabImages } from "actions/customization-actions";
import { useCustomizationModalContext } from "../context/customization-modal-context";

import { DATATEST } from "constants/integration";
import { useSkipFirstEffect } from "hooks";
const { CUSTOMIZATIONS } = DATATEST;

export interface ITabsManagerProps {
    product: IProduct;
    attribute: IAttribute;
    selected?: TCustomizationSelection;
    merchantId?: string;
    permissions: IPrintedImagePermission[];
    onChange: (image: ICustomizationImageEntity) => void;
    onSelectImage: (selectedImage: ICustomizationImageEntity | null) => void;
    onTabChanged: (tabId: number) => void;
    onRemoveSelectedImage: () => void;
}

export interface IStateProps {
    existingImages: (ICustomizationImageEntity | null)[];
    verifiedImages: ICustomizationImageEntity[] | null;
    isFetchingExistingImages: boolean;
}

export interface IDispatchProps {
    onResetTabImages: () => void;
}

export const TabsManagerContainer: React.FC<ITabsManagerProps & IStateProps & IDispatchProps> = ({
    product,
    permissions,
    selected,
    merchantId,
    onChange,
    onSelectImage,
    onTabChanged,
    attribute,
    onRemoveSelectedImage,
    verifiedImages,
    onResetTabImages,
    isFetchingExistingImages,
}) => {
    const classes = useStyles();
    const [selectedTab, setSelectedTab] = useState(getInitialImageGroup(permissions));
    const [tabChecked, setTabChecked] = useState(false);
    const [tabIsClicked, setTabIsClicked] = useState(false);
    const { buyerPermission, brandPermission } = getUserPermissions(permissions);
    const { checkTab } = useCustomizationModalContext();

    const changeTab = (value: ImagePermissionType) => {
        if (typeof value !== "number") {
            return;
        }

        onTabChanged(value);
        setSelectedTab(value);
    };

    const handleChangeTab = (event: unknown, value: ImagePermissionType) => {
        setTabIsClicked(true);
        changeTab(value);
    };

    useSkipFirstEffect(() => {
        if (isFetchingExistingImages || !tabIsClicked) {
            setTabIsClicked(false);
            return;
        }

        onRemoveSelectedImage();
        setTabIsClicked(false);
    }, [selectedTab, isFetchingExistingImages, tabIsClicked]);

    useEffect(() => {
        if (tabChecked || !selected?.value || selected?.type !== CustomizationSelectionType.PRINTED_IMAGE) {
            return;
        }

        checkTab(merchantId, [selected.value]);
        setTabChecked(true);
    }, [merchantId, selected?.value]);

    useEffect(() => {
        if (verifiedImages === null) {
            return;
        }

        changeTab(verifiedImages?.[0]?.id === selected?.value ? ImagePermissionType.BUYER : ImagePermissionType.BRAND);
        onResetTabImages();
    }, [verifiedImages, selected?.value]);

    const renderTab = (label: string, dataTest: string) => (
        <Tab data-test={dataTest} classes={{ root: classes.tabRoot }} label={label} />
    );

    const renderImageList = (index: number, tabType: ImagePermissionType) => (
        <TabPanel value={selectedTab} index={index} key={index}>
            <ImagesList
                selected={selected}
                tabType={tabType}
                merchantId={merchantId}
                onChange={onChange}
                onSelectImage={onSelectImage}
                product={product}
                attribute={attribute}
                onRemoveSelectedImage={onRemoveSelectedImage}
            />
        </TabPanel>
    );

    return (
        <Box>
            {permissions?.length > 1 && (
                <Tabs
                    classes={{ root: classes.root, indicator: classes.tabsIndicator }}
                    onChange={handleChangeTab}
                    value={selectedTab}
                    data-test={CUSTOMIZATIONS.TABS_CONTAINER}
                >
                    {!buyerPermission
                        ? null
                        : renderTab(
                              buyerPermission.name || format("Uploaded Logos"),
                              CUSTOMIZATIONS.EXISTING_IMAGE_BUYER_TAB
                          )}
                    {!brandPermission
                        ? null
                        : renderTab(
                              brandPermission.name || format("Approved Logos"),
                              CUSTOMIZATIONS.EXISTING_IMAGE_BRAND_TAB
                          )}
                </Tabs>
            )}
            <Box>
                {[ImagePermissionType.BUYER, ImagePermissionType.BRAND].map((type, index) =>
                    renderImageList(index, type)
                )}
            </Box>
        </Box>
    );
};

const mapStateToProps = (state: IStore): IStateProps => {
    const { existingImages, imagesTab } = state.customization;
    return {
        isFetchingExistingImages: existingImages?.isFetching,
        existingImages: existingImages?.payload?.images,
        verifiedImages: imagesTab?.verifiedImages,
    };
};

const mapDispatchToProps = (dispatch: Dispatch<IStore>): IDispatchProps => ({
    onResetTabImages: () => dispatch(resetCheckedImageTabImages()),
});

export const TabsManager = connect<IStateProps, IDispatchProps, ITabsManagerProps>(
    mapStateToProps,
    mapDispatchToProps
)(TabsManagerContainer);

const useStyles = makeStyles({
    tabRoot: {
        color: "black",
        fontFamily: "inherit",
        fontSize: "16px",
        fontWeight: "bold",
        textTransform: "none",
    },
    tabsIndicator: {
        backgroundColor: "#2dbeb4",
    },
    root: {
        marginBottom: "10px",
    },
});
